import React, { useState, useMemo } from 'react';

interface SearchQuery {
  query_id: string;
  text: string;
  attempt: number;
  timestamp: string;
}

interface SearchResult {
  query_id: string;
  rank: number;
  url: string;
  title: string;
  snippet: string;
  timestamp: string;
}

interface SearchProgressBoxProps {
  isVisible: boolean;
  queries: SearchQuery[];
  results: SearchResult[];
  status: 'starting' | 'searching' | 'completed' | 'error' | 'cancelled';
  provider?: string;
  totalResults?: number;
  duration?: number;
  error?: string;
}

const SearchProgressBox: React.FC<SearchProgressBoxProps> = ({
  isVisible,
  queries,
  results,
  status,
  provider,
  totalResults,
  duration,
  error
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // クエリテキストの集合（ハイライト用）
  const queryTerms = useMemo(() => {
    const tokens = new Set<string>();
    for (const q of queries) {
      (q.text || '')
        .split(/\s+/)
        .map(t => t.trim())
        .filter(t => t.length >= 3)
        .forEach(t => tokens.add(t.toLowerCase()));
    }
    return Array.from(tokens).slice(0, 10);
  }, [queries]);

  const highlightText = (text: string) => {
    if (!text) return text;
    if (queryTerms.length === 0) return text;
    const regex = new RegExp(`(${queryTerms.map(t => escapeRegExp(t)).join('|')})`, 'ig');
    const parts = text.split(regex);
    return parts.map((part, idx) => {
      const match = queryTerms.find(t => part.toLowerCase() === t);
      if (match) {
        return <mark key={idx} className="bg-yellow-200">{part}</mark>;
      }
      return <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  function escapeRegExp(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // noop
    }
  };

  // クエリごとの完了状態を判定（結果が1件以上あれば完了）
  const getQueryCompleted = (queryId: string): boolean => {
    return results.some(r => r.query_id === queryId);
  };

  const completedCount = queries.filter(q => getQueryCompleted(q.query_id)).length;
  const isSearching = status === 'starting' || status === 'searching';

  if (!isVisible) return null;

  return (
    <div className="space-y-3" role="region" aria-live="polite">
      {/* ヘッダー: タスク進行状況スタイル */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <span className="font-medium text-gray-900">実行中のタスク</span>
          <span className="text-gray-400">|</span>
          <span className="text-sm text-gray-600">
            {completedCount}/{queries.length}件完了
          </span>
          {provider && <span className="text-xs text-gray-500">({provider})</span>}
        </div>
        <span className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </div>

      {/* タスクリスト: thinking-indicator.tsx 風 */}
      <div className="space-y-2.5">
        {queries.map((query) => {
          const isCompleted = getQueryCompleted(query.query_id);
          return (
            <div key={`${query.query_id}-${query.attempt}`} className="flex items-center space-x-3 text-sm">
              {isCompleted ? (
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              ) : (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin flex-shrink-0"></div>
              )}
              <span className={`${isCompleted ? "text-gray-500" : "text-gray-700"}`}>
                {query.text}
              </span>
            </div>
          );
        })}

        {/* 検索中かつクエリがまだない場合 */}
        {isSearching && queries.length === 0 && (
          <div className="flex items-center space-x-3 text-sm">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin flex-shrink-0"></div>
            <span className="text-gray-700">検索クエリを準備中...</span>
          </div>
        )}
      </div>

      {/* 展開時の詳細表示 */}
      {isExpanded && (
        <div className="mt-3 space-y-3">
          {/* サマリー */}
          {totalResults !== undefined && duration !== undefined && (
            <div className="text-xs text-green-700 bg-green-50 p-2 rounded">
              {totalResults}件の結果を{duration}msで取得
            </div>
          )}

          {/* エラー表示 */}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 p-3 rounded border-l-3 border-red-500">
              エラー: {error}
            </div>
          )}

          {/* 検索結果 */}
          {results.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs text-gray-500 font-medium">検索結果 ({results.length}件)</h4>
              {results.map((result, index) => (
                <div
                  key={`${result.query_id}-${result.rank}-${index}`}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="p-2 flex items-center gap-2">
                    <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {result.rank}
                    </span>
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-blue-600 hover:underline font-medium text-sm truncate"
                    >
                      {highlightText(result.title)}
                    </a>
                    <button
                      onClick={(e) => { e.preventDefault(); handleCopy(result.url); }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      aria-label="リンクをコピー"
                      title="リンクをコピー"
                    >
                      ⧉
                    </button>
                  </div>
                  <div className="px-2 pb-1 text-xs text-green-600 truncate">
                    {result.url}
                  </div>
                  {result.snippet && (
                    <div className="px-2 pb-2 text-xs text-gray-600 line-clamp-2">
                      {highlightText(result.snippet)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchProgressBox;
